import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { http, imgUrl, errMsg } from "@/lib/api";
import { FieldInput } from "./FieldInput";

const ItemForm = ({ fields, item, onClose, onSave }) => {
  const [form, setForm] = useState(item);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto border-[#163D43] bg-[#071D1F] text-teal-50" data-testid="admin-item-dialog">
        <DialogHeader><DialogTitle className="font-display">{item.id ? "Editar" : "Novo item"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {fields.map((f) => (
            <div key={f.key} className="space-y-1.5">
              <label className="text-xs uppercase tracking-wider text-teal-300">{f.label}</label>
              <FieldInput f={f} value={form[f.key]} onChange={(v) => set(f.key, v)} />
            </div>
          ))}
        </div>
        <button onClick={() => onSave(form)} data-testid="admin-item-save" className="mt-2 w-full rounded-full bg-[#2DD4BF] py-3 font-semibold text-[#041214]">Salvar</button>
      </DialogContent>
    </Dialog>
  );
};

export const ResourceManager = ({ name, fields, titleKey, defaults }) => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => http.get(`/admin/${name}`).then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
  }, [name]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (form) => {
    const { id, ...body } = form;
    body.features = (body.features || []).filter(Boolean);
    try {
      if (id) await http.put(`/admin/${name}/${id}`, body);
      else await http.post(`/admin/${name}`, body);
      toast.success("Salvo!");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async (it) => {
    if (!window.confirm(`Excluir "${it[titleKey]}"?`)) return;
    await http.delete(`/admin/${name}/${it.id}`).catch((e) => toast.error(errMsg(e)));
    load();
  };

  return (
    <div data-testid={`admin-${name}-manager`}>
      <button onClick={() => setEditing({ ...defaults, order: items.length })} data-testid={`admin-${name}-add`} className="mb-6 inline-flex items-center gap-2 rounded-full bg-[#2DD4BF] px-5 py-2.5 text-sm font-semibold text-[#041214]">
        <Plus className="h-4 w-4" /> Adicionar
      </button>
      <div className="grid gap-3">
        {items.map((it, i) => (
          <div key={it.id} className="flex items-center gap-4 rounded-md border border-[#163D43] bg-[#0B2326] p-3" data-testid={`admin-${name}-row-${i}`}>
            {it.image_url ? <img src={imgUrl(it.image_url)} alt="" className="h-14 w-14 rounded object-cover" /> : <div className="h-14 w-14 rounded bg-[#163D43]" />}
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold text-teal-50">{it[titleKey]}</p>
              <p className="truncate text-sm text-teal-300">{[it.category || it.tag, it.price].filter(Boolean).join(" · ") || "—"}</p>
            </div>
            <button onClick={() => setEditing(it)} className="rounded-full p-2 text-teal-200 hover:bg-[#163D43]" data-testid={`admin-${name}-edit-${i}`} aria-label="Editar"><Pencil className="h-4 w-4" /></button>
            <button onClick={() => remove(it)} className="rounded-full p-2 text-red-300 hover:bg-[#163D43]" data-testid={`admin-${name}-delete-${i}`} aria-label="Excluir"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      {editing && <ItemForm fields={fields} item={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
};
